import React, { useEffect, useState } from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import useConfirmationDialogState from 'Hooks/useConfirmationDialogState';
import fr from 'facet-rest';
import NewDialog from 'Components/UI/ConfirmationDialog/NewDialog';
import { dataAttrs } from 'Helpers/Testing';
import { MetaResourceRenderer } from 'Components/UI/Meta/MetaForm/Components/MetaResourceRenderer';
import {
  BalanceSheetAccountLayout,
} from 'Views/Page/Content/Modal/Layouts/FinancialInstitutionConnectionLayout/Layout/AccountLayout';
import { useSnackbarContext } from 'Context/SnackbarContext';
import {
  metaOverride,
} from 'Views/Page/Content/Modal/Layouts/FinancialInstitutionConnectionLayout/Layout/AccountLayout/metaOverride';
import { FDLozenge, FDStack } from '@facet-wealth/design';
import MetaControlField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import FinancialInstitutionImagePicker
  from 'Views/Onboarding/Pages/Survey/SurveyStep/Components/FinancialInstitutionImagePicker';
import { useSurveyContext } from 'Views/Onboarding/Pages/Survey/SurveyContext';

const Layout = () => {
  const { clearModalCollection } = useModalContext();
  const [currentAccountIndex, setCurrentAccountIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editResource, setEditResource] = useState(null);
  const { displayErrorMessage } = useSnackbarContext();
  const { refreshOnboardingStepCollection } = useSurveyContext();

  const {
    resource,
  } = useMetaLayoutContext();

  const {
    handleClose: closeDialog,
  } = useConfirmationDialogState({});

  // Get accounts from the resource
  const accounts = resource.attr('accounts').resources().list() || [];
  const currentAccount = accounts[currentAccountIndex];

  const handleClose = () => {
    refreshOnboardingStepCollection();
    closeDialog();
    clearModalCollection();
  };

  // Handle no accounts
  useEffect(() => {
    if (resource && resource.id() > 0 && accounts.length === 0) {
      handleClose();
    }
  }, [accounts.length, resource]);

  useEffect(() => {
    if (!currentAccount) return;

    currentAccount.withRelationship(
      fr.relationships.balanceSheetEditAccount.name,
      async (rel) => {
        try {
          setLoading(true);
          const editCollection = await rel.link('related').get();
          setEditResource(editCollection.resources().head());
          setLoading(false);
        } catch (err) {
          setLoading(false);
          displayErrorMessage(err, err?.errors || 'Failed to load account.');
        }
      },
      () => {},
    );
  }, [currentAccountIndex, currentAccount]);

  if (!accounts.length || !editResource) {
    return null;
  }

  const accountNumber = currentAccountIndex + 1;
  const totalAccounts = accounts.length;

  const handleConfirm = async () => {
    try {
      await editResource.submit();
      // Move to the next account or close if this is the last one
      if (currentAccountIndex < accounts.length - 1) {
        setCurrentAccountIndex(currentAccountIndex + 1);
      } else {
        handleClose();
      }
    } catch (err) {
      displayErrorMessage(err, err?.errors || 'Failed to update account.');
    }
  };

  const handleCancel = () => {
    // Move to the previous account if not on the first one
    if (currentAccountIndex > 0) {
      setCurrentAccountIndex(currentAccountIndex - 1);
    }
  };

  return (
    <NewDialog
      titleComponent={
        <FDLozenge
          label={`Account ${accountNumber} of ${totalAccounts}`}
          color={'info'}
          size={'lg'}
          {...dataAttrs('step-text-lozenge')}
        />
      }
      logo={
      resource.withRelationship(fr.relationships.logo.name, () => (
        <FinancialInstitutionImagePicker
          resource={resource}
          width={'24'}
          height={'24'}
          alt={''}
        />
      ), () => null)
      }
      showCloseIcon
      cancelLabel={'Previous'}
      confirmLabel={'Next'}
      confirmButtonType="info"
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      canCancelViaSecondaryButton={currentAccountIndex !== 0}
      loading={loading}
      message=""
      onClose={() => {
        handleClose();
      }}
      {...dataAttrs('financial-institution-connection-modal')}
      maxWidth={'xs'}
      fullWidth
    >
      <FDStack direction={'column'} spacing={2}>
        <MetaControlField attr={'title'} />
        <MetaResourceRenderer
          resource={editResource}
          layout={BalanceSheetAccountLayout}
          metaOverrideSet={metaOverride}
        />
      </FDStack>
    </NewDialog>
  );
};

export default Layout;
